import { motion } from 'framer-motion';
import { PRODUCT_THEME } from '../lib/products';
import { cx, isFresh, relativeTime } from '../lib/format';
import type { Suggestion } from '../lib/types';
import {
  Avatar,
  CategoryChip,
  CommentIcon,
  ImageIcon,
  PriorityMeter,
  ProductChip,
  StatusChip,
  VisibilityChip,
} from './primitives';

/**
 * A suggestion at a glance. Two signals are deliberately loud:
 *   - the product hue, carried as a left edge stripe and the chip colour
 *   - urgency: priority >= 4 and unresolved gets a slow breathing glow, and
 *     anything under 48h old gets a NEW ribbon.
 * Everything else stays quiet so those two read instantly across a full grid.
 */
export function SuggestionCard({
  suggestion,
  onOpen,
  index = 0,
}: {
  suggestion: Suggestion;
  onOpen: (id: string) => void;
  index?: number;
}) {
  const theme = PRODUCT_THEME[suggestion.product];
  const urgent = suggestion.priority >= 4 && suggestion.status !== 'resolved';
  const fresh = isFresh(suggestion.createdAt);
  const commentCount = suggestion._count?.comments ?? 0;

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      // Exit carries its own fast transition. Inheriting the enter stagger here
      // would make cards linger for up to 0.75s after a filter change, which
      // reads as lag rather than polish.
      exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.15, delay: 0 } }}
      transition={{
        duration: 0.4,
        // Cascade the grid in, capped so a large page does not crawl.
        delay: Math.min(index * 0.035, 0.35),
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ y: -4 }}
      className={cx(
        // h-full + the footer's mt-auto keep every card in a row the same
        // height with its footer on the same baseline. Letting cards hug their
        // content instead was tried and looked worse: it trades a little
        // breathing room inside short cards for ragged voids between rows.
        'group surface relative flex h-full cursor-pointer flex-col overflow-hidden',
        'transition-[border-color,box-shadow] duration-300 hover:border-[var(--hairline-strong)]',
        urgent && 'animate-priority',
      )}
      onClick={() => onOpen(suggestion.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(suggestion.id);
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`Open suggestion: ${suggestion.title}`}
    >
      {/* Product hue as a left edge — the fastest scan signal in the grid. */}
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-0 w-[3px] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
        style={{ background: `linear-gradient(180deg, ${theme.hex}, transparent 85%)` }}
      />

      {suggestion.imageUrl && (
        <div className="hairline relative h-32 shrink-0 overflow-hidden border-b">
          <img
            src={suggestion.imageUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-900/85 via-ink-900/10 to-transparent" />
        </div>
      )}

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex flex-wrap items-center gap-1.5">
          <ProductChip product={suggestion.product} />
          <CategoryChip category={suggestion.category} />
          {fresh && (
            <span className="chip border-lotus-400/40 bg-lotus-400/12 text-lotus-200">NEW</span>
          )}
        </div>

        <h3 className="text-h3 font-semibold text-mist-50 clamp-2 text-balance">
          {suggestion.title}
        </h3>

        <p className="text-sm leading-relaxed text-mist-400 clamp-3 text-pretty">
          {suggestion.description}
        </p>

        <div className="mt-auto space-y-3 pt-2">
          <div className="flex items-center justify-between gap-3">
            <StatusChip status={suggestion.status} />
            <PriorityMeter value={suggestion.priority} />
          </div>

          <div className="hairline flex items-center justify-between gap-3 border-t pt-3">
            <div className="flex min-w-0 items-center gap-2">
              <Avatar user={suggestion.submittedBy} size={24} />
              <div className="min-w-0">
                <p className="truncate text-xs font-medium text-mist-200">
                  {suggestion.submittedBy.name}
                </p>
                <p className="text-[0.6875rem] text-mist-600">
                  {relativeTime(suggestion.createdAt)}
                </p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2.5 text-mist-500">
              {suggestion.imageUrl && <ImageIcon className="h-3.5 w-3.5" />}
              {commentCount > 0 && (
                <span className="flex items-center gap-1 text-[0.6875rem] font-medium">
                  <CommentIcon className="h-3.5 w-3.5" />
                  {commentCount}
                </span>
              )}
              {suggestion.assignedTo ? (
                <span className="flex items-center gap-1.5" title={`Assigned to ${suggestion.assignedTo.name}`}>
                  <span className="text-[0.6875rem] text-mist-600">→</span>
                  <Avatar user={suggestion.assignedTo} size={22} ring />
                </span>
              ) : (
                <span className="chip chip-idle text-mist-600">Unassigned</span>
              )}
            </div>
          </div>

          {suggestion.visibility === 'private' && (
            <div className="flex">
              <VisibilityChip visibility="private" />
            </div>
          )}
        </div>
      </div>
    </motion.article>
  );
}
